import React, { useEffect, useState } from "react";
import "./GamesSlider.css";
import { formatBoardStartTime, parseBoardStartDate } from "../timeDisplay";
import { getWorldCupCountryFlag } from "./worldCupFlags";
const { isLiveGame, getEventAvailabilityKey, filterRejectedEvents } = require("./gameAvailability.js");

const PLACEHOLDER_GAMES = Array.from({ length: 10 }, (_, index) => ({ id: `placeholder-${index}` }));
const MINIMUM_VISIBLE_GAMES = 4;
const GAMES_CACHE_STORAGE_KEY = "ww_games_slider_cache_v12";
const GAMES_CACHE_TTL_MS = 2 * 60 * 1000;
const LIVE_GAMES_CACHE_TTL_MS = 2 * 60 * 1000;
const GAMES_CACHE_MAX_ENTRIES = 40;
const gamesMemoryCache = new Map();
const SPORT_ICON_CLASSES = {
  nba: "fa-solid fa-basketball",
  nba2h: "fa-solid fa-basketball",
  march: "fa-solid fa-basketball",
  cbb: "fa-solid fa-basketball",
  cbb2h: "fa-solid fa-basketball",
  wnba: "fa-solid fa-basketball",
  wnba1h: "fa-solid fa-basketball",
  wcbb: "fa-solid fa-basketball",
  nfl: "fa-solid fa-football",
  nflp: "fa-solid fa-football",
  nflszn: "fa-solid fa-football",
  cfb: "fa-solid fa-football",
  cfl: "fa-solid fa-football",
  mlb: "fa-solid fa-baseball",
  kbo: "fa-solid fa-baseball",
  wbc: "fa-solid fa-baseball",
  nhl: "fa-solid fa-hockey-puck",
  ufc: "fa-solid fa-hand-fist",
  pga: "fa-solid fa-golf-ball-tee",
  soccer: "fa-solid fa-futbol",
  worldcup: "fa-solid fa-futbol",
  worldcup1h: "fa-solid fa-futbol",
  worldcup2h: "fa-solid fa-futbol",
  cs2: "fa-solid fa-crosshairs",
  lol: "fa-solid fa-dragon",
  val: "fa-solid fa-shield",
  cod: "fa-solid fa-crosshairs",
  dota2: "fa-solid fa-gamepad",
  r6: "fa-solid fa-shield",
  tennis: "fa-solid fa-table-tennis",
  f1: "fa-solid fa-flag-checkered",
  darts: "fa-solid fa-bullseye",
  boxing: "fa-solid fa-hand-fist",
  nascar: "fa-solid fa-flag-checkered",
  cricket: "fa-solid fa-baseball",
};
const buildTbdGames = (gamesCount) =>
  Array.from(
    { length: Math.max(0, MINIMUM_VISIBLE_GAMES - gamesCount) },
    (_, index) => ({ id: `tbd-${index}` })
  );

const getSportCacheKey = (sport, shrk = "0") => `${String(sport || "").trim().toLowerCase()}|${String(shrk) === "1" ? "1" : "0"}`;

const getGameStartDate = (game) => parseBoardStartDate(game?.start_time_iso || game?.start_time || "");

const filterFutureGames = (games, now = Date.now()) => {
  const list = Array.isArray(games) ? games : [];

  return list
    .filter((game) => {
      const startDate = getGameStartDate(game);
      return isLiveGame(game) || !startDate || startDate.getTime() > now;
    })
    .sort((left, right) => {
      const leftStart = getGameStartDate(left)?.getTime();
      const rightStart = getGameStartDate(right)?.getTime();

      if (Number.isFinite(leftStart) && Number.isFinite(rightStart)) {
        return leftStart - rightStart;
      }
      if (Number.isFinite(leftStart)) {
        return -1;
      }
      if (Number.isFinite(rightStart)) {
        return 1;
      }

      return 0;
    });
};

const getGamesCacheTtl = (games) => (
  (Array.isArray(games) && games.some(isLiveGame)) ? LIVE_GAMES_CACHE_TTL_MS : GAMES_CACHE_TTL_MS
);

const readPersistedGamesCache = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return { version: 1, entries: {} };
  }

  try {
    const raw = window.localStorage.getItem(GAMES_CACHE_STORAGE_KEY);
    if (!raw) {
      return { version: 1, entries: {} };
    }

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== 1 || !parsed.entries || typeof parsed.entries !== "object") {
      return { version: 1, entries: {} };
    }

    return parsed;
  } catch (e) {
    return { version: 1, entries: {} };
  }
};

const writePersistedGamesCache = (state) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    window.localStorage.setItem(GAMES_CACHE_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    const entries = Object.entries(state.entries || {})
      .sort(([, left], [, right]) => Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0))
      .slice(0, Math.max(8, Math.floor(GAMES_CACHE_MAX_ENTRIES / 2)));

    try {
      window.localStorage.setItem(GAMES_CACHE_STORAGE_KEY, JSON.stringify({ version: 1, entries: Object.fromEntries(entries) }));
    } catch (retryError) {
      // Storage quota/private-mode failures should not block the game rail.
    }
  }
};

const pruneGamesCacheEntries = (state, now = Date.now()) => {
  const entries = Object.entries(state.entries || {})
    .map(([key, entry]) => {
      const games = filterFutureGames(entry?.games || [], now);
      return [key, { ...entry, games }];
    })
    .filter(([, entry]) => {
      const cachedAt = Number(entry?.cachedAt || 0);
      return cachedAt > 0
        && now - cachedAt <= getGamesCacheTtl(entry?.games)
        && Array.isArray(entry.games)
        && entry.games.length > 0;
    })
    .sort(([, left], [, right]) => Number(right?.cachedAt || 0) - Number(left?.cachedAt || 0))
    .slice(0, GAMES_CACHE_MAX_ENTRIES);

  return { version: 1, entries: Object.fromEntries(entries) };
};

const getCachedGames = (sport, shrk = "0") => {
  const cacheKey = getSportCacheKey(sport, shrk);
  if (!cacheKey) {
    return [];
  }

  const now = Date.now();
  const memoryEntry = gamesMemoryCache.get(cacheKey);
  if (memoryEntry && now - Number(memoryEntry.cachedAt || 0) <= getGamesCacheTtl(memoryEntry.games)) {
    // Feed rebuilds may replace a live event's filter id without changing its
    // matchup. Never expose a persisted live card before the current rail
    // response confirms the id, or the athlete request targets a dead event.
    const games = filterFutureGames(memoryEntry.games, now).filter((game) => !isLiveGame(game));
    if (games.length > 0) {
      gamesMemoryCache.set(cacheKey, { cachedAt: memoryEntry.cachedAt, games });
      return games;
    }
    gamesMemoryCache.delete(cacheKey);
  }

  const state = pruneGamesCacheEntries(readPersistedGamesCache(), now);
  const entry = state.entries[cacheKey];
  writePersistedGamesCache(state);

  if (!entry) {
    return [];
  }

  gamesMemoryCache.set(cacheKey, {
    cachedAt: Number(entry.cachedAt || now),
    games: entry.games,
  });

  return filterFutureGames(entry.games, now).filter((game) => !isLiveGame(game));
};

export const clearCachedGamesForSport = (sport) => {
  const sportKey = String(sport || "").trim().toLowerCase();
  if (!sportKey) {
    return;
  }

  gamesMemoryCache.delete(`${sportKey}|0`);
  gamesMemoryCache.delete(`${sportKey}|1`);
  const state = pruneGamesCacheEntries(readPersistedGamesCache());
  delete state.entries[`${sportKey}|0`];
  delete state.entries[`${sportKey}|1`];
  writePersistedGamesCache(state);
};

const setCachedGames = (sport, games, shrk = "0") => {
  const cacheKey = getSportCacheKey(sport, shrk);
  const freshGames = filterFutureGames(games);
  if (!cacheKey) {
    return;
  }

  if (freshGames.length === 0) {
    clearCachedGamesForSport(sport);
    return;
  }

  const now = Date.now();
  gamesMemoryCache.set(cacheKey, {
    cachedAt: now,
    games: freshGames,
  });

  const state = pruneGamesCacheEntries(readPersistedGamesCache(), now);
  state.entries[cacheKey] = {
    cachedAt: now,
    games: freshGames,
  };
  writePersistedGamesCache(pruneGamesCacheEntries(state, now));
};

const getSportIconClassName = (sport) => {
  const sportKey = String(sport || "").trim().toLowerCase();
  return SPORT_ICON_CLASSES[sportKey] || "fa-solid fa-trophy";
};

const getPeriodTeamNames = (sport, home, away) => {
  const sportKey = String(sport || "").trim().toLowerCase();
  const homeName = String(home || "").trim();
  const awayName = String(away || "").trim();
  const periodMatch = `${homeName} ${awayName}`.match(/\b(1st|first|2nd|second)\s+half\b/i);
  let periodLabel = "";

  if (periodMatch) {
    periodLabel = ["1st", "first"].includes(periodMatch[1].toLowerCase()) ? "1st Half" : "2nd Half";
  } else if (/1h$/.test(sportKey)) {
    periodLabel = "1st Half";
  } else if (/2h$/.test(sportKey)) {
    periodLabel = "2nd Half";
  }

  if (!periodLabel) {
    return { home: homeName, away: awayName };
  }

  const stripPeriodLabel = (teamName) => teamName.replace(
    /\s+\b(?:1st|first|2nd|second)\s+half\b\s*$/i,
    ""
  ).trim();
  const normalizedHome = stripPeriodLabel(homeName);
  const normalizedAway = stripPeriodLabel(awayName);

  return {
    home: normalizedHome ? `${normalizedHome} ${periodLabel}` : normalizedHome,
    away: normalizedAway ? `${normalizedAway} ${periodLabel}` : normalizedAway,
  };
};

const GameTeam = ({ name, logo, flagEmoji, sport, score = null, showFallbackIcon = true }) => (
  <div className={`ww-games-card__team${!logo && !flagEmoji && !showFallbackIcon ? " ww-games-card__team--no-logo" : ""}`}>
    {flagEmoji ? (
      <span className="ww-games-card__team-flag" aria-hidden="true">
        {flagEmoji}
      </span>
    ) : logo ? (
      <img
        className="ww-games-card__team-logo"
        src={logo}
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
      />
    ) : showFallbackIcon ? (
      <i aria-hidden="true" className={`${getSportIconClassName(sport)} ww-inline-icon ww-games-card__team-icon`} />
    ) : (
      null
    )}
    <span className="ww-games-card__team-name">{name}</span>
    {score !== null && score !== undefined && score !== "" && (
      <span className="ww-games-card__team-score" aria-label={`${name} score ${score}`}>{score}</span>
    )}
  </div>
);

const formatGameStart = (startIso, fallback = "") => {
  return formatBoardStartTime(startIso, {
    fallback,
    todayLabel: "Today",
  });
};

const GamesSlider = ({
  sport,
  shrk = "0",
  selectedGameId,
  onSelectGame,
  games: providedGames = null,
  pending = false,
  excludeGameId = "",
  label = "",
  showTbd = true,
  onInventoryChange = null,
  onLiveAvailabilityChange = null,
  rejectedEvents = {},
  pendingEventKey = "",
}) => {
  const usingProvidedGames = Array.isArray(providedGames);
  const [games, setGames] = useState(() => (usingProvidedGames ? providedGames : []));
  const [loading, setLoading] = useState(() => Boolean(sport) && !usingProvidedGames);
  const [error, setError] = useState("");
  const [liveRefreshTick, setLiveRefreshTick] = useState(0);
  const [confirmedGamesSport, setConfirmedGamesSport] = useState("");

  useEffect(() => {
    // Poll even an empty/upcoming-only rail: otherwise it never discovers the
    // first live game until the customer changes leagues or reloads the page.
    if (usingProvidedGames || !sport) {
      return undefined;
    }

    const timeout = window.setTimeout(() => setLiveRefreshTick((tick) => tick + 1), 60 * 1000);
    return () => window.clearTimeout(timeout);
  }, [sport, usingProvidedGames, liveRefreshTick]);

  useEffect(() => {
    if (usingProvidedGames) {
      setGames(filterFutureGames(providedGames));
      setLoading(false);
      setError("");
      return undefined;
    }

    if (!sport) {
      setGames([]);
      setLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    const cachedGames = getCachedGames(sport, shrk);
    const hasCachedGames = cachedGames.length > 0;

    setConfirmedGamesSport("");
    setGames(cachedGames);
    setLoading(!hasCachedGames);
    setError("");

    const query = new URLSearchParams({ shrk: String(shrk) === "1" ? "1" : "0" });
    fetch(`/wp-json/wagedwar/v1/available-games/${encodeURIComponent(sport)}?${query.toString()}`, {
      method: "GET",
      credentials: "same-origin",
      signal: controller.signal,
    })
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) {
          throw new Error(json?.message || "Failed to load games");
        }
        return {
          json,
          inventoryStatus: String(res.headers.get("X-WW-Board-Inventory") || "").toLowerCase(),
        };
      })
      .then(({ json, inventoryStatus }) => {
        if (!json.success) {
          if (!hasCachedGames) {
            setGames([]);
            setError(json.data || "Failed to load games");
          }
          return;
        }

        if (typeof onInventoryChange === "function" && ["available", "empty"].includes(inventoryStatus)) {
          onInventoryChange(sport, inventoryStatus === "available");
        }

        const freshGames = filterFutureGames(json.data);
        setGames(freshGames);
        setConfirmedGamesSport(sport);
        setCachedGames(sport, freshGames, shrk);
      })
      .catch((err) => {
        if (err?.name === "AbortError") {
          return;
        }

        console.error(err);
        if (!hasCachedGames) {
          setGames([]);
          setError("Failed to load games");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [sport, shrk, usingProvidedGames, providedGames, liveRefreshTick, onInventoryChange]);

  useEffect(() => {
    if (usingProvidedGames || confirmedGamesSport !== sport || loading || error
      || typeof onLiveAvailabilityChange !== "function") return;
    const selectableGames = filterRejectedEvents(games, rejectedEvents, sport)
      .filter((game) => String(game?.id || "") !== String(excludeGameId || ""));
    onLiveAvailabilityChange(sport, selectableGames.some(isLiveGame));
  }, [games, rejectedEvents, sport, excludeGameId, confirmedGamesSport, loading, error, usingProvidedGames, onLiveAvailabilityChange]);

  if (!sport) {
    return null;
  }

  // Top Picks receives its event rail from the athlete response rather than
  // fetching it here. Keep the rail's reserved height while that response is
  // pending so the promo and card sections do not jump upward between leagues.
  const showPlaceholders = loading || (usingProvidedGames && pending);
  const visibleGames = filterRejectedEvents(games, rejectedEvents, sport)
    .filter((game) => String(game?.id || "") !== String(excludeGameId || ""));
  const tbdGames = !loading && !error && !usingProvidedGames && showTbd && visibleGames.length > 0
    ? buildTbdGames(visibleGames.length)
    : [];
  const sliderItems = showPlaceholders ? PLACEHOLDER_GAMES : [...visibleGames, ...tbdGames];

  if (!showPlaceholders && !error && sliderItems.length === 0) {
    return null;
  }

  const shellClassName = [
    "ww-games-slider-shell",
    showPlaceholders ? "is-loading" : "",
    !showPlaceholders && !error && sliderItems.length > 0 ? "is-ready" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shellClassName}>
      {label && <div className="ww-board-section-label">{label}</div>}
      {error && <div className="ww-games-slider-status ww-games-slider-status--error">{error}</div>}

      {!error && sliderItems.length > 0 && (
        <div className="ww-games-slider" role="list" aria-label="Available games">
          {sliderItems.map((game) => {
            if (showPlaceholders) {
              return (
                <div key={game.id} className="ww-games-slider__slide" role="listitem">
                  <div className="ww-games-card ww-games-card--placeholder" aria-hidden="true">
                    <div className="ww-games-card__body">
                      <div className="ww-games-card__team">
                        <span className="ww-games-card__placeholder ww-games-card__placeholder--logo" />
                      </div>
                      <div className="ww-games-card__team ww-games-card__team--no-logo">
                      </div>
                    </div>
                    <div className="ww-games-card__footer">
                    </div>
                  </div>
                </div>
              );
            }

            if (String(game.id).startsWith("tbd-")) {
              return (
                <div key={game.id} className="ww-games-slider__slide" role="listitem">
                  <button
                    type="button"
                    className="ww-games-card ww-games-card--tbd"
                    aria-label="Game to be determined"
                    disabled
                  >
                    <div className="ww-games-card__body">
                      <GameTeam name="TBD" showFallbackIcon={false} />
                      <GameTeam name="TBD" showFallbackIcon={false} />
                    </div>

                    <div className="ww-games-card__footer">
                      <span className="ww-games-card__time">TBD</span>
                    </div>
                  </button>
                </div>
              );
            }

            const isSelected = String(selectedGameId || "") === String(game.id || "");
            const isLive = isLiveGame(game);
            const isChecking = pendingEventKey === getEventAvailabilityKey(game, sport);
            const sourceSport = String(game?._topPlaysLeague || sport || "").toLowerCase();
            const useCountryFlags = ["worldcup", "worldcup1h", "worldcup2h"].includes(sourceSport);
            const periodTeamNames = getPeriodTeamNames(sourceSport, game.home, game.away);

            return (
              <div key={game.id} className="ww-games-slider__slide" role="listitem">
                <button
                  type="button"
                  className={`ww-games-card${isSelected ? " is-selected" : ""}${isLive ? " is-live" : ""}`}
                  onClick={() => onSelectGame && onSelectGame(game)}
                  disabled={isChecking}
                  aria-busy={isChecking || undefined}
                >
                  <div className="ww-games-card__body">
                    <GameTeam
                      name={periodTeamNames.home}
                      logo={useCountryFlags ? "" : game.home_logo}
                      flagEmoji={useCountryFlags ? getWorldCupCountryFlag(game.home) : ""}
                      sport={sourceSport}
                      score={isLive ? game.home_score : null}
                    />
                    <GameTeam
                      name={periodTeamNames.away}
                      logo={useCountryFlags ? "" : game.away_logo}
                      flagEmoji={useCountryFlags ? getWorldCupCountryFlag(game.away) : ""}
                      sport={sourceSport}
                      score={isLive ? game.away_score : null}
                    />
                  </div>

                  <div className={`ww-games-card__footer${isChecking ? " is-checking" : ""}`}>
                    {isChecking ? (
                      <span
                        className="ww-event-check-spinner"
                        role="status"
                        aria-label="Checking event availability"
                      />
                    ) : isLive ? (
                      <span className="ww-games-card__live-indicator" aria-label="Live event">
                        <span className="ww-games-card__live-dot" aria-hidden="true" />
                        Live
                      </span>
                    ) : (
                      <span className="ww-games-card__time">
                        {formatGameStart(game.start_time_iso, game.start_time)}
                      </span>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GamesSlider;
